import { StatusType } from "document/StatusTypes";
import { Operation } from "rfc6902/diff";
import {
	checkStatus,
	extractHeader,
	getType,
	patch,
	post,
	put
} from "utils/Service";
import { getBlueprintTypeSuffix } from "./Utils";

export const BLUEPRINT_ENDPOINT = "/api/blueprints";
const CONTENT_SUFFIX = "content";
const RIGHTS_SUFFIX = "rights";

// TODO: correct naming convention.
export enum BlueprintType {
	COMPETENCE_LIST = "COMPETENCE_LIST",
	EXTRACT = "EXTRACT",
	REPORT = "REPORT",
	TRAJECTORY = "TRAJECTORY",
	DASHBOARD = "DASHBOARD",
	INFORMATION = "INFORMATION"
}

export enum Right {
	ADMIN = "ADMIN",
	WRITE = "WRITE",
	READ = "READ"
}

export type UserRights = {
	user: string;
	rights: Right[];
};

export type BlueprintInfo = {
	id: number;
	name: string;
	description: string;
	type: BlueprintType;
	created: string; // ISO format
	modified: string; // ISO format
	version: number;
	rights: Right[];

	status: StatusType;
	statusModifiedOn: string;
	statusModifiedBy: number;
	statusModifiedByEmail: string;

	library: boolean;
	sourceBlueprintId: number | null;
	sourceBlueprintVersion: number | null;
};

export type CreateBlueprintInfo = {
	name: string;
	description: string;
	userRights: UserRights[];
};

export type UpdateBlueprintInfo = {
	name: string;
	description: string;
};

export function getBlueprints(): Promise<BlueprintInfo[]> {
	return getType(BLUEPRINT_ENDPOINT);
}

export function getBlueprintsByType(
	type: BlueprintType
): Promise<BlueprintInfo[]> {
	return getType(`${BLUEPRINT_ENDPOINT}/${getBlueprintTypeSuffix(type)}`);
}

export function getBlueprintInfo(id: number): Promise<BlueprintInfo> {
	return getType(`${BLUEPRINT_ENDPOINT}/${id}`);
}

export function getBlueprintRights(id: number): Promise<UserRights[]> {
	return getType(`${BLUEPRINT_ENDPOINT}/${id}/${RIGHTS_SUFFIX}`);
}

export function updateBlueprintInfo(
	id: number,
	info: UpdateBlueprintInfo
): Promise<void> {
	return put(`${BLUEPRINT_ENDPOINT}/${id}`, info).then(response => {
		checkStatus(response, 200);
	});
}

export function updateBlueprintRights(
	id: number,
	content: UserRights[]
): Promise<void> {
	return put(`${BLUEPRINT_ENDPOINT}/${id}/${RIGHTS_SUFFIX}`, content).then(
		response => {
			checkStatus(response, 200);
		}
	);
}

// Only to be used by more specific blueprint services.
export function createBlueprint(
	pathSuffix: string,
	info: CreateBlueprintInfo
): Promise<string> {
	return post(`${BLUEPRINT_ENDPOINT}/${pathSuffix}`, info).then(response => {
		checkStatus(response, 201);
		return extractHeader(response.headers, "location");
	});
}

export function getBlueprintContent<T>(
	pathSuffix: string,
	id: number
): Promise<T> {
	return getType(
		`${BLUEPRINT_ENDPOINT}/${pathSuffix}/${id}/${CONTENT_SUFFIX}`
	);
}

export function updateBlueprintContent(
	pathSuffix: string,
	id: number,
	content: Operation[]
): Promise<void> {
	return patch(
		`${BLUEPRINT_ENDPOINT}/${pathSuffix}/${id}/${CONTENT_SUFFIX}`,
		content
	).then(response => {
		checkStatus(response, 200);
	});
}

export function getBlueprintEndpoint<T>(endpoint: string): Promise<T> {
	return getType(`${BLUEPRINT_ENDPOINT}/${endpoint}`);
}
